import { clearUserInfo, getUserInfo } from "@/utils/storage";
import type { RequestOptions } from "@@/plugin-request/request";
import type { RequestConfig } from "@umijs/max";
import { history } from "@umijs/max"; // 用于页面跳转
import { message as antdMessage, Modal as antdModal } from "antd";

// 配置静态方法，避免 context 警告
antdMessage.config({
  top: 100,
  duration: 2,
  maxCount: 3,
});

// 使用别名以避免混淆
const message = antdMessage;
const Modal = antdModal;

// 用于防止重复弹出403错误的标志
let isShowing403Modal = false;

// 扩展RequestOptions类型，添加skipAuth选项
interface ExtendedRequestOptions extends RequestOptions {
  skipAuth?: boolean;
}
// 与后端约定的响应数据格式
interface ResponseStructure {
  success: boolean;
  data: any;
  errorCode?: number;
  errorMsg?: string;
}

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      const { errorCode, errorMsg } = res as unknown as ResponseStructure;

      // 判断是否为失败状态：如果 errorCode 不等于 200（或你后端约定的成功码），则视为失败
      if (errorCode !== 200) {
        const error: any = new Error(errorMsg || "请求失败");
        error.name = "BizError";
        // 将 errorCode 和 errorMsg 以及原始数据存入 info
        error.info = { errorCode, errorMsg };
        throw error; // 抛出自制的错误
      }
    },
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      // 我们的 errorThrower 抛出的错误。
      if (error.name === "BizError") {
        const errorInfo: ResponseStructure | undefined = error.info;
        if (errorInfo) {
          const { errorCode, errorMsg } = errorInfo;
          console.log("errorInfo", errorInfo);

          // 👇👇👇 根据 errorCode 进行特殊处理 👇👇👇
          switch (errorCode) {
            case 403: // 权限不足
              // 防止重复弹出403错误框
              if (!isShowing403Modal) {
                isShowing403Modal = true;
                Modal.warning({
                  title: "请重新登录",
                  content: errorMsg || "您没有权限访问此资源，请先登录。",
                  onOk: () => {
                    clearUserInfo(); // 清空存储
                    // 获取当前页面信息用于重定向
                    const { search, pathname } = window.location;
                    const urlParams = new URL(window.location.href)
                      .searchParams;
                    const searchParams = new URLSearchParams({
                      redirect: pathname + search,
                    });
                    /** 此方法会跳转到 redirect 参数所在的位置 */
                    const redirect = urlParams.get("redirect");
                    // Note: There may be security issues, please note
                    if (
                      window.location.pathname !== "/user/login" &&
                      !redirect
                    ) {
                      history.replace({
                        pathname: "/user/login",
                        search: searchParams.toString(),
                      });
                    }
                    isShowing403Modal = false; // 重置标志
                  },
                  onCancel: () => {
                    isShowing403Modal = false; // 重置标志
                  },
                });
              }
              return; // 处理完后直接返回，不再走下面的通用处理

            case 404: // 资源未找到
              message.error(errorMsg);
              return;

            case 500: // 服务器内部错误
              message.error(errorMsg);
              return;
          }
        }
      } else if (error.response) {
        console.log(error);
        // Axios 的错误
        // 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围
        message.error(`网络异常: ${error.response.status}`);
      } else if (error.request) {
        // 请求已经成功发起，但没有收到响应
        message.error("网络连接失败，请检查您的网络设置。");
      } else {
        // 发送请求时出了点问题
        message.error("请求发生未知错误，请重试。");
      }
    },
  },
  // ✅ 添加 Content-Type 请求头
  headers: {
    "Content-Type": "application/json;charset=utf-8",
  },

  // 请求拦截器
  requestInterceptors: [
    (config: ExtendedRequestOptions) => {
      // 检查是否需要跳过token验证（如登录接口）
      const skipAuth = config.skipAuth || false;

      if (!skipAuth) {
        // 自动携带token
        const userInfo = getUserInfo();
        if (userInfo && userInfo.token) {
          // 在请求头中添加token
          config.headers = {
            ...config.headers,
            Authorization: `Bearer ${userInfo.token}`,
          };
        } else {
          // 如果获取不到 token，抛出一个 403 错误，让 errorHandler 统一处理跳转逻辑
          const error: any = new Error("请重新登录");
          error.name = "BizError";
          error.info = {
            errorCode: 403,
            errorMsg: "无效的登录凭证,请重新登录",
          };
          // 返回被拒绝的 Promise，让 errorHandler 中的 403 case 捕获并处理跳转
          return Promise.reject(error);
        }
      }

      return { ...config };
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response) => {
      // // 拦截响应数据，进行个性化处理
      // const { data } = response as unknown as ResponseStructure;

      // if (data?.success === false) {
      //   message.error("请求失败！");
      // }
      return response;
    },
  ],
};
